import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  CardActionArea,
  Avatar,
  useTheme,
  Tooltip,
  Badge,
  Alert,
} from '@mui/material';
import { List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InsightsIcon from '@mui/icons-material/Insights';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import GavelIcon from '@mui/icons-material/Gavel';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchAppInfo, fetchHealthStatus } from '../store/slices/appSlice';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiService } from '../services/apiService';
import { motion } from 'framer-motion';
import ActivityLineChart, { ActivityPoint } from '../components/Charts/ActivityLineChart';
import TopBarChart, { TopItem } from '../components/Charts/TopBarChart';
import BipartisanDonut, { DonutSlice } from '../components/Charts/BipartisanDonut';
import ShareIcon from '@mui/icons-material/Share';
import MemberCard from '../components/Members/MemberCard';
import LatestVotesCardList from '../components/Votes/LatestVotesCardList';
import { useAuth } from '../context/AuthContext';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { appInfo, healthStatus, loading } = useSelector((state: RootState) => state.app);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { authenticated, initialized, login } = useAuth();

  const [trends, setTrends] = useState<any | null>(null);
  const [stats, setStats] = useState<any | null>(null);
  const [trendingIssues, setTrendingIssues] = useState<any[]>([]);
  const [latestBills, setLatestBills] = useState<any[]>([]);
  const [billsLoading, setBillsLoading] = useState(true);
  const [billsError, setBillsError] = useState(false);
  const [dashboardAttempt, setDashboardAttempt] = useState(0);
  const [latestVotes, setLatestVotes] = useState<any[]>([]);
  const [trendingLawmakers, setTrendingLawmakers] = useState<any[]>([]);
  const [trendingFollowed, setTrendingFollowed] = useState<any[] | null>(null);
  const [myFeed, setMyFeed] = useState<any[] | null>(null);
  const [myInsights, setMyInsights] = useState<any | null>(null);

  // Local relevance: location and reps
  const [zipInput, setZipInput] = useState<string>("");
  const [myLocation, setMyLocation] = useState<any | null>(null);
  const [myReps, setMyReps] = useState<any[]>([]);
  const [locLoading, setLocLoading] = useState<boolean>(false);
  const [locError, setLocError] = useState<string | null>(null);
  // Best-effort summaries for local reps (AI member summaries, short snippet)
  const [repSummaries, setRepSummaries] = useState<Record<string, string>>({});

  // Derive a better "Votes Happening Now" count: prefer backend stats, fallback to items updated very recently
  const votesNowInfo = React.useMemo(() => {
    const apiCount = Number(stats?.votes_happening_now || 0);
    const items = Array.isArray(latestVotes) ? latestVotes : [];
    const now = Date.now();
    // Consider votes updated within the last 15 minutes as "happening now"
    const windowMs = 15 * 60 * 1000;
    const recentCount = items.filter((v: any) => {
      const ts = v?.updateDate || v?.startDate;
      if (!ts) return false;
      const t = new Date(ts).getTime();
      return isFinite(t) && now - t <= windowMs;
    }).length;
    if (apiCount > 0) return { count: apiCount, source: 'api' as const };
    if (recentCount > 0) return { count: recentCount, source: 'fallback' as const };
    return { count: 0, source: 'api' as const };
  }, [stats?.votes_happening_now, latestVotes]);

  useEffect(() => {
    dispatch(fetchAppInfo());
    dispatch(fetchHealthStatus());
    let cancelled = false;
    setBillsLoading(true);
    setBillsError(false);
    // Each section renders independently; an optional feed cannot erase bills.
    apiService.getPublicStatsSummary().then(r => { if (!cancelled) setStats(r.data); }).catch(() => {});
    apiService.getTrendingIssues(8).then(r => { if (!cancelled) setTrendingIssues(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    apiService.getPublicBills({ filter: 'latest', limit: 10 }).then(r => {
      if (!Array.isArray(r.data?.items)) throw new Error('Invalid bill response');
      if (!cancelled) setLatestBills(r.data.items);
    }).catch(() => { if (!cancelled) setBillsError(true); })
      .finally(() => { if (!cancelled) setBillsLoading(false); });
    apiService.getLatestVotes(8).then(r => { if (!cancelled) setLatestVotes(Array.isArray(r.data?.items) ? r.data.items : []); }).catch(() => {});
    apiService.getPublicLawmakers({ limit: 6 }).then(r => {
      if (!cancelled) setTrendingLawmakers(Array.isArray(r.data?.items) ? r.data.items : (Array.isArray(r.data) ? r.data : []));
    }).catch(() => {});
    apiService.getTrendingFollowedBills(8).then(r => { if (!cancelled) setTrendingFollowed(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [dispatch, dashboardAttempt]);

  useEffect(() => {
    let cancelled = false;
    apiService.getPublicTrends().then(resp => { if (!cancelled) setTrends(resp.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load my location for local relevance (if authenticated)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authenticated) return;
      try {
        const resp = await apiService.getMyLocation();
        if (cancelled) return;
        const loc = resp?.data?.location || null;
        setMyLocation(loc);
        setZipInput((loc?.zip || "") as string);
        setMyReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  // For signed-out users, remember the last ZIP locally and auto-load reps on revisit
  useEffect(() => {
    let cancelled = false;
    if (authenticated) return;
    try {
      const savedZip = window.localStorage.getItem('abt_last_zip') || '';
      if (savedZip && /^\d{5,9}$/.test(savedZip)) {
        setZipInput(savedZip);
        (async () => {
          try {
            const resp = await apiService.getPublicLocationByZip(savedZip);
            if (cancelled) return;
            setMyLocation(resp?.data?.location || null);
            setMyReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
          } catch {
            // ignore
          }
        })();
      }
    } catch {}
    return () => { cancelled = true; };
  }, [authenticated]);

  // Fetch personal feed and insights when authenticated
  useEffect(() => {
    if (!initialized) return;
    if (!authenticated) {
      setMyFeed(null);
      setMyInsights(null);
      // Clear location-only auth state
      setMyLocation(null);
      setMyReps([]);
      return;
    }
    (async () => {
      try {
        const [feedResp, insightsResp] = await Promise.all([
          apiService.getMyFeed(10, 0),
          apiService.getMyInsights(),
        ]);
        setMyFeed(Array.isArray(feedResp.data?.items) ? feedResp.data.items : []);
        setMyInsights(insightsResp.data || {});
      } catch (e) {
        setMyFeed([]);
        setMyInsights({});
      }
    })();
  }, [authenticated, initialized]);

  // Enrich reps with short AI summary snippets (best-effort, non-blocking)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const reps = Array.isArray(myReps) ? myReps : [];
        const ids = reps.map((r: any) => r.bioguide_id).filter(Boolean);
        if (ids.length === 0) { setRepSummaries({}); return; }
        const next: Record<string, string> = {};
        await Promise.all(ids.map(async (id: string) => {
          try {
            const resp = await apiService.getMemberAISummary(id);
            const text = (resp?.data?.summary || '').toString();
            if (text) {
              // Take first paragraph or 200 chars
              let snippet = text.split(/\n\n+/)[0].trim();
              if (snippet.length > 200) snippet = snippet.slice(0, 197) + '…';
              next[id] = snippet;
            }
          } catch {}
        }));
        if (!cancelled) setRepSummaries(next);
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [JSON.stringify(myReps.map((r: any) => r?.bioguide_id || ''))]);

  // SEO: set page title and description (must be declared before any conditional return)
  useEffect(() => {
    document.title = 'Congress Right Now | AskTheBill.ai';
    const meta = document.querySelector('meta[name="description"]');
    const description = 'Explore the latest bills, votes, and trending issues in Congress. Data-backed insights and AI summaries to help you stay informed.';
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      const m = document.createElement('meta');
      (m as any).name = 'description';
      (m as any).content = description;
      document.head.appendChild(m);
    }
  }, []);

  if (loading && !appInfo && !healthStatus) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(33,150,243,0.22), rgba(156,39,176,0.15))'
            : 'linear-gradient(135deg, rgba(33,150,243,0.12), rgba(255,193,7,0.12))',
          border: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Congress Right Now
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 900 }}>
            Track live legislative activity, browse recent bills, and see how bipartisan support is shaping up across the aisle. Fresh data, clear insights, and AI summaries.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <Button component={RouterLink as any} to="/bills" variant="contained" size="medium">
              Browse Bills
            </Button>
            <Button component={RouterLink as any} to="/members" variant="outlined" size="medium">
              Explore Members
            </Button>
            <Button component={RouterLink as any} to="/chat" variant="text" size="medium" startIcon={<ShareIcon fontSize="small" /> }>
              Ask About a Bill
            </Button>
          </Stack>
        </motion.div>
        {/* Hot Button Issues banner */}
        {trendingIssues && trendingIssues.length > 0 && (
          <Paper elevation={0} sx={{ p: 1.5, mt: 1.5, borderRadius: 2, backgroundColor: theme => theme.palette.background.paper, border: theme => `1px dashed ${theme.palette.divider}` }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <WhatshotIcon color="warning" />
              <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 600 }}>Hot Button Issues:</Typography>
              {trendingIssues.slice(0, 8).map((issue: any, idx: number) => (
                <Chip key={idx} label={issue.title} component={RouterLink as any} to={`/issues/${issue.slug}`} clickable sx={{ mr: 1, mb: 1 }} />
              ))}
            </Stack>
          </Paper>
        )}
      </Box>

      {/* Hero Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
            }}
          >
            <CardActionArea component={RouterLink} to="/bills?view=introduced_today" aria-label="View bills introduced today">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                    <InsightsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Bills Introduced Today</Typography>
                    <Typography variant="h4">{stats?.bills_introduced_today ?? '—'}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
            }}
          >
            <CardActionArea component={RouterLink} to="/bills?view=passed_week" aria-label="View bills passed this week">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) }}>
                    <GavelIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Bills Passed This Week</Typography>
                    <Typography variant="h4">{stats?.bills_passed_this_week ?? '—'}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
            }}
          >
            <CardActionArea component={RouterLink} to="/bills?view=votes_now" aria-label="View bills with votes happening now">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, color: theme.palette.getContrastText(theme.palette.warning.main) }}>
                    <HowToVoteIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Votes Happening Now</Typography>
                    {votesNowInfo.source === 'fallback' ? (
                      <Tooltip title="Estimated from recent vote updates (last 15 min)">
                        <Typography variant="h4">{votesNowInfo.count}</Typography>
                      </Tooltip>
                    ) : (
                        <Typography variant="h4">{votesNowInfo.count}</Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      {stats?.last_updated && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          Last updated: {new Date(stats.last_updated).toLocaleString()}
        </Typography>
      )}

      {/* AI Headlines Summary (last 24h) */}
      {stats && (
        <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <WhatshotIcon color="warning" />
              <Typography variant="h6">Headlines (last {stats?.headlines_window_hours ?? 24}h)</Typography>
            </Stack>
            {(() => {
              const raw = typeof stats?.headlines_summary === 'string' ? stats.headlines_summary : '';
              const text: string = String(raw || '').trim();
              const hasHeadlines = text.length > 0;
              if (!hasHeadlines) {
                const hours = Number(stats?.headlines_window_hours || 24);
                return (
                  <Typography variant="body2" color="textSecondary">
                    No qualifying bill activity in the last {hours} hours, or the AI summary is temporarily unavailable.
                  </Typography>
                );
              }
              const hasBullets = /^\s*[\-•]/m.test(text);
              if (hasBullets) {
                const lines = text
                  .split(/\n+/)
                  .map(l => l.replace(/^\s*[\-•]\s*/, '').trim())
                  .filter(l => l.length > 0);
                return (
                  <List dense sx={{ pl: 1 }}>
                    {lines.map((l, idx) => (
                      <ListItem key={idx} sx={{ py: 0.25 }}>
                        <ListItemText primary={`• ${l}`} />
                      </ListItem>
                    ))}
                  </List>
                );
              }
              // Render as paragraphs (split by blank lines)
              const paras = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
              return (
                <Box>
                  {paras.map((p, i) => (
                    <Typography key={i} variant="body1" paragraph sx={{ mb: 1.2 }}>
                      {p}
                    </Typography>
                  ))}
                </Box>
              );
            })()}
            {stats?.headlines_generated_at && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                Generated: {new Date(stats.headlines_generated_at).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Latest Bills (preview) */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, mb: 1 }}>
        <Typography variant="h5" component="h2">Recently introduced bills</Typography>
        <Button component={RouterLink} to="/bills">Browse all bills</Button>
      </Stack>
      {billsLoading && <Typography role="status" sx={{ mb: 2 }}>Loading recent bills…</Typography>}
      {billsError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setDashboardAttempt(n => n + 1)}>Retry</Button>}>
        Recent bills could not be refreshed. {latestBills.length > 0 ? 'Showing the last loaded bills.' : 'Please retry to load the latest bills.'}
      </Alert>}
      <Grid container spacing={2}>
        {(latestBills || []).slice(0, 6).map((bill: any, idx: number) => {
          const [congress, billType, billNumber] = typeof bill.id === 'string' ? bill.id.split('/') : [];
          const to = congress && billType && billNumber ? `/bills/${congress}/${billType}/${billNumber}` : undefined;
          const shareUrl = to ? `${window.location.origin}${to}` : window.location.origin;
          const shareText = encodeURIComponent(`Check out this bill: ${bill.title || bill.id}`);
          const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`;
          const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          return (
            <Grid item xs={12} md={4} key={idx}>
              <Card sx={{ borderRadius: 3, height: '100%', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }} >
                {to ? (
                  <CardActionArea component={RouterLink} to={to}>
                    <CardContent>
                      <Typography variant="overline" color="primary">{billType?.toUpperCase()} {billNumber} · Congress {congress}</Typography>
                      <Typography variant="subtitle1" component="h3" gutterBottom>{bill.title}</Typography>
                      {bill.introduced_date && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Introduced {new Date(`${bill.introduced_date}T12:00:00`).toLocaleDateString()}</Typography>}
                      <Typography variant="body2" color="textSecondary">{bill.summary_short}</Typography>
                    </CardContent>
                  </CardActionArea>
                ) : (
                  <CardContent>
                    <Typography variant="overline" color="primary">{billType?.toUpperCase()} {billNumber} · Congress {congress}</Typography>
                      <Typography variant="subtitle1" component="h3" gutterBottom>{bill.title}</Typography>
                      {bill.introduced_date && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Introduced {new Date(`${bill.introduced_date}T12:00:00`).toLocaleDateString()}</Typography>}
                    <Typography variant="body2" color="textSecondary">{bill.summary_short}</Typography>
                  </CardContent>
                )}
                <CardContent>
                  <Stack direction="row" spacing={1}>
                    {to && (
                      <Button variant="contained" color="primary" component={RouterLink} to={to}>
                        Learn More
                      </Button>
                    )}
                    <Button variant="outlined" color="secondary" href={twitterUrl} target="_blank" rel="noopener noreferrer" startIcon={<ShareIcon />}>
                      Share
                    </Button>
                    <Button variant="outlined" href={fbUrl} target="_blank" rel="noopener noreferrer">
                      Facebook
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {!billsLoading && !billsError && latestBills.length === 0 && (
          <Grid item xs={12}><Typography color="textSecondary">No recently introduced bills are available yet.</Typography></Grid>
        )}
      </Grid>

      {/* Personalized Sections */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <TrendingUpIcon color="secondary" />
                <Typography variant="h6" component="h2">For You</Typography>
              </Stack>
              {authenticated ? (
                <>
                  {myFeed === null && (
                    <Typography variant="body2" color="textSecondary">Loading your feed…</Typography>
                  )}
                  {Array.isArray(myFeed) && myFeed.length > 0 && (
                    <List dense>
                      {myFeed.slice(0, 6).map((item: any, idx: number) => {
                        const b = item.bill || {};
                        const to = (b.congress && b.bill_type && b.bill_number) ? `/bills/${b.congress}/${b.bill_type}/${b.bill_number}` : undefined;
                        return (
                          <ListItem key={idx} component={to ? (RouterLink as any) : 'li'} to={to || ''}>
                            <ListItemText
                              primary={b.title || `${b.bill_type?.toUpperCase()}.${b.bill_number}`}
                              secondary={b.latest_action_text || b.latest_action_date}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                  {Array.isArray(myFeed) && myFeed.length === 0 && (
                    <Typography variant="body2" color="textSecondary">Follow bills to see personalized updates here.</Typography>
                  )}
                </>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">Sign in to get your personal feed of followed bill updates.</Typography>
                  <Button variant="contained" onClick={login}>Sign In</Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <InsightsIcon color="info" />
                <Typography variant="h6" component="h2">My Insights</Typography>
              </Stack>
              {authenticated ? (
                !myInsights ? (
                  <Typography variant="body2" color="textSecondary">Loading your insights…</Typography>
                ) : (
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Follows</Typography>
                      <Typography variant="h5">{myInsights?.stats?.follows?.total ?? 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Bookmarks</Typography>
                      <Typography variant="h5">{myInsights?.stats?.bookmarks?.total ?? 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Comments</Typography>
                      <Typography variant="h6">{myInsights?.stats?.comments ?? 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Reactions</Typography>
                      <Typography variant="h6">{myInsights?.stats?.reactions ?? 0}</Typography>
                    </Grid>
                  </Grid>
                )
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">Sign in to see your engagement stats and party mix.</Typography>
                  <Button variant="contained" onClick={login}>Sign In</Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Local relevance card: ZIP → Reps */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                <Typography variant="h6">Local</Typography>
                <Typography variant="body2" color="textSecondary">Enter your ZIP to see your representatives.</Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box component="form" onSubmit={(e: any) => { e.preventDefault(); }} sx={{ display: 'flex', gap: 1 }}>
                  <input style={{ display: 'none' }} />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <input
                    aria-label="ZIP"
                    value={zipInput}
                    onChange={(e: any) => setZipInput(e.target.value)}
                    placeholder="ZIP"
                    style={{ padding: 8, borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)', maxWidth: 140 }}
                  />
                  <Button
                    variant="contained"
                    disabled={locLoading}
                    onClick={async () => {
                      setLocError(null);
                      if (!zipInput || !/^\d{5,9}$/.test(zipInput)) { setLocError('Enter a valid 5 or 9 digit ZIP'); return; }
                      setLocLoading(true);
                      try {
                        if (authenticated) {
                          const resp = await apiService.setMyLocation(zipInput);
                          setMyLocation(resp?.data?.location || null);
                          setMyReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
                        } else {
                          const resp = await apiService.getPublicLocationByZip(zipInput);
                          setMyLocation(resp?.data?.location || null);
                          setMyReps(Array.isArray(resp?.data?.representatives) ? resp.data.representatives : []);
                          try { window.localStorage.setItem('abt_last_zip', zipInput); } catch {}
                        }
                      } catch (e: any) {
                        setLocError(e?.response?.data?.detail || 'Failed to resolve ZIP');
                      } finally {
                        setLocLoading(false);
                      }
                    }}
                  >
                    {authenticated ? 'Save ZIP' : 'Show Reps'}
                  </Button>
                </Stack>
              </Stack>
              {locError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{locError}</Typography>}
              {myLocation?.state && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Using location: {myLocation.state}{myLocation.district ? `-${myLocation.district}` : ''}
                </Typography>
              )}
              {myReps.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Your Representatives</Typography>
                  <List dense>
                    {myReps.map((r: any, idx: number) => {
                      const path = r.bioguide_id ? `/members/${r.bioguide_id}` : undefined;
                      const summary = repSummaries[r.bioguide_id || ''] || '';
                      return (
                        <ListItem key={`${r.bioguide_id || r.name}:${idx}`} divider component={path ? (RouterLink as any) : 'li'} to={path || ''} sx={{ alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={`${r.name} ${r.party ? `(${r.party})` : ''}`}
                            secondary={
                              <>
                                <span>{r.state}{r.district ? `-${r.district}` : ''}</span>
                                {summary && (
                                  <>
                                    <br />
                                    <span style={{ color: 'inherit' }}>{summary}</span>
                                  </>
                                )}
                              </>
                            }
                          />
                          {r.role && (
                            <Chip size="small" label={r.role === 'senator' ? 'Senator' : 'Representative'} color={r.role === 'senator' ? 'info' : 'success'} />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Latest Votes and Trending Lawmakers side-by-side */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <HowToVoteIcon color="warning" />
                <Typography variant="h6" component="h2">Latest Votes</Typography>
              </Stack>
              {latestVotes && latestVotes.length > 0 ? (
                <LatestVotesCardList items={latestVotes} />
              ) : (
                <Typography variant="body2" color="textSecondary">No recent votes.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" component="h2">Trending Lawmakers</Typography>
              </Stack>
              <Grid container spacing={2}>
                {(trendingLawmakers || []).slice(0, 6).map((m: any, idx: number) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <MemberCard member={m} />
                  </Grid>
                ))}
                {trendingLawmakers && trendingLawmakers.length === 0 && (
                  <Grid item xs={12}><Typography color="textSecondary">No trending lawmakers.</Typography></Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts & Trends */}
      {(() => {
        const activity: ActivityPoint[] = trends?.weekly_activity || [];
        const topBars: TopItem[] = trends?.top_cosponsors || [];

        // Build bipartisan donut from backend stats only (no fake defaults)
        const r = Number(stats?.bipartisan?.republican ?? 0);
        const d = Number(stats?.bipartisan?.democrat ?? 0);
        const i = Number(stats?.bipartisan?.independent ?? 0);
        const donut: DonutSlice[] = [
          { name: 'Republican', value: r, color: '#d32f2f' },
          { name: 'Democrat', value: d, color: '#1976d2' },
          { name: 'Independent', value: i, color: '#6b7280' },
        ].filter(s => s.value > 0);

        return (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>Legislative Activity (Weekly)</Typography>
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Typography variant="caption">Bills introduced per week (UTC); current week is partial.</Typography>
                    {activity.length ? <ActivityLineChart data={activity} /> : <Typography>Activity data unavailable.</Typography>}
                  </motion.div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>Bipartisan Split</Typography>
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                    <BipartisanDonut data={donut} />
                  </motion.div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>Top Bills by Cosponsors</Typography>
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
                    <Typography variant="caption">Congress {trends?.congress || '—'} • Recorded cosponsor counts</Typography>
                    {topBars.length ? <TopBarChart data={topBars} /> : <Typography>Cosponsor data unavailable.</Typography>}
                  </motion.div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      })()}

      {/* Trending Issues */}
      <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>Trending Issues</Typography>
      <Grid container spacing={2}>
        {(trendingIssues || []).map((issue: any, idx: number) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1">{issue.title}</Typography>
                <Button size="small" component={RouterLink} to={`/issues/${issue.slug}`} sx={{ mt: 1 }}>View</Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {trendingIssues && trendingIssues.length === 0 && (
          <Grid item xs={12}><Typography color="textSecondary">No trending issues.</Typography></Grid>
        )}
      </Grid>

      {/* Most Followed Bills */}
      <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>Most Followed Bills</Typography>
      <Grid container spacing={2}>
        {Array.isArray(trendingFollowed) && trendingFollowed.length > 0 && trendingFollowed.slice(0, 8).map((b: any, idx: number) => {
          const to = (b && b.id) ? `/bills/${b.id}` : undefined;
          return (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card>
                <CardActionArea component={to ? (RouterLink as any) : 'div'} to={to || ''}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>{b.title}</Typography>
                    <Typography variant="body2" color="textSecondary">Followers: {b.follower_count ?? 0}</Typography>
                    {b.latest_action_text && (
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>{b.latest_action_text}</Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
        {Array.isArray(trendingFollowed) && trendingFollowed.length === 0 && (
          <Grid item xs={12}><Typography color="textSecondary">No follower data yet.</Typography></Grid>
        )}
        {trendingFollowed === null && (
          <Grid item xs={12}><Typography color="textSecondary">Loading…</Typography></Grid>
        )}
      </Grid>

      </Box>
    );
};

export default Dashboard;
